import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { Subject } from 'rxjs';
import { FilesUploadsApiService } from './files-uploads-api.service';
import { UploadChanges } from '../models/UploadChanges';
import { FileUploadState } from '../models/FileUploadState';
import { FilesApiService } from './files-api.service';
import { FileCategory } from '../models/FileCategories';
import { AttachmentModel } from '@fc-vehicles/service/attachment-model';

@Injectable({
  providedIn: 'root',
})
export class FilesUploadsService {
  private readonly filesApi = inject(FilesApiService);
  private readonly uploadService = inject(FilesUploadsApiService);
  private readonly injector = inject(Injector);

  private readonly _uploads = signal<FileUploadState[]>([]);
  private readonly uploadDiscardedSubject = new Subject<number>();
  readonly uploads = this._uploads.asReadonly();
  readonly uploadDiscarded$ = this.uploadDiscardedSubject.asObservable();

  readonly totalProgress = computed(() => {
    const uploads = this._uploads();
    if (uploads.length === 0) return 0;

    const totalSize = uploads.reduce((sum, upload) => sum + upload.fileSize, 0);
    if (totalSize === 0) return 0;

    const weightedProgress = uploads.reduce(
      (sum, upload) => sum + upload.fileSize * upload.progress,
      0,
    );
    return Math.round(weightedProgress / totalSize);
  });

  readonly totalSize = computed(() => {
    const uploads = this._uploads();
    const totalSize = uploads.reduce((total, upload) => {
      return total + upload.fileSize;
    }, 0);
    return formatFileSize(totalSize);
  });

  readonly uploadedSize = computed(() => {
    const uploads = this._uploads();
    const totalSize = uploads.reduce((total, upload) => {
      return total + (upload.fileSize * upload.progress) / 100;
    }, 0);
    return formatFileSize(totalSize);
  });

  readonly uploadCounts = computed(() => {
    const uploads = this._uploads();
    return {
      uploading: uploads.filter((u) => u.status === 'uploading').length,
      completed: uploads.filter((u) => u.status === 'completed').length,
      failed: uploads.filter((u) => u.status === 'failed').length,
      total: uploads.length,
    };
  });

  readonly hasActiveUploads = computed(() => {
    return this._uploads().some((u) => u.status === 'uploading');
  });

  async uploadFiles(
    files: FileList | File[],
    category: FileCategory,
  ): Promise<number[]> {
    const fileArray = Array.from(files);
    const fileSlots = await Promise.all(
      fileArray.map(async () => await this.filesApi.createFileSlot(category)),
    );

    const uploadsWithSlots = fileArray.map((file, index) => ({
      file,
      slot: fileSlots[index],
    }));

    const validUploads = uploadsWithSlots.filter(
      ({ slot }) => slot?.id != null,
    );

    const invalidUploads = uploadsWithSlots.length - validUploads.length;
    if (invalidUploads > 0) {
      console.error(
        `Failed to allocate upload slots for ${invalidUploads} file(s).`,
      );
    }

    const uploadPromises = validUploads.map(({ file, slot }) =>
      this.startUpload(file, slot),
    );
    await Promise.all(uploadPromises);
    return validUploads.map(({ slot }) => slot.id!);
  }

  async ngUploadFiles(
    files: FileList | File[],
    category: FileCategory,
    isRecording = false,
  ): Promise<AttachmentModel[]> {
    const fileArray = Array.from(files);
    const fileSlots = await Promise.all(
      fileArray.map(
        async () => await this.filesApi.createFileSlot(category, isRecording),
      ),
    );

    const uploadsWithSlots = fileArray.map((file, index) => ({
      file,
      slot: fileSlots[index],
    }));

    const validUploads = uploadsWithSlots.filter(
      ({ slot }) => slot?.id != null,
    );

    const invalidUploads = uploadsWithSlots.length - validUploads.length;
    if (invalidUploads > 0) {
      console.error(
        `Failed to allocate upload slots for ${invalidUploads} file(s).`,
      );
    }

    const uploadPromises = validUploads.map(({ file, slot }) =>
      this.startUpload(file, slot),
    );
    await Promise.all(uploadPromises);
    return fileSlots;
  }

  async retryUpload(id: number): Promise<void> {
    const upload = this._uploads().find((u) => u.id === id);
    if (!upload || upload.status !== 'failed') return;

    this.removeUpload(id);
    await this.startUpload(upload.file, upload.id);
  }

  cancelUpload(id: number): void {
    const success = this.uploadService.cancelUpload(id);
    if (success) {
      this.removeUpload(id);
    }
  }

  discardUpload(id: number): void {
    const hasUpload = this._uploads().some((upload) => upload.id === id);
    this.uploadService.cancelUpload(id);
    this.removeUpload(id);

    if (hasUpload) {
      this.uploadDiscardedSubject.next(id);
    }
  }

  cancelAllUploads(): void {
    this.uploadService.cancelAllUploads();
    this._uploads.set([]);
  }

  clearCompletedUploads(): void {
    this._uploads.update((uploads) =>
      uploads.filter((upload) => upload.status !== 'completed'),
    );
  }

  clear() {
    this.clearCompletedUploads();
  }

  removeUpload(id: number): void {
    this._uploads.update((uploads) =>
      uploads.filter((upload) => upload.id !== id),
    );
  }

  private async startUpload(
    file: File,
    slot: number | FileUploadState['media'],
  ): Promise<void> {
    const slotId =
      typeof slot === 'number' ? slot : slot?.id != null ? slot.id : undefined;

    if (slotId == null) {
      console.error('Failed to start upload: slot id is missing');
      return;
    }

    try {
      const { changes } = await this.uploadService.uploadFile(file, slotId);
      const formattedFileSize = formatFileSize(file.size);

      const uploadState: FileUploadState = {
        id: slotId,
        fileName: file.name,
        fileSize: file.size,
        formattedFileSize,
        status: 'uploading',
        progress: 0,
        file,
        media: typeof slot === 'number' ? undefined : (slot ?? undefined),
      };

      this._uploads.update((uploads) => [...uploads, uploadState]);

      this.trackUploadChanges(slotId, changes);
    } catch (error) {
      console.error('Failed to start upload:', error);
    }
  }

  private trackUploadChanges(
    slotId: number,
    changes: WritableSignal<UploadChanges>,
  ): void {
    effect(
      () => {
        const changeData = changes();

        untracked(() => {
          this.updateUploadState(slotId, changeData);
          if (changeData.type === 'success') {
            void this.loadFileDetails(slotId);
          }
        });
      },
      { injector: this.injector },
    );
  }

  private updateUploadState(slotId: number, changes: UploadChanges): void {
    this._uploads.update((uploads) =>
      uploads.map((upload) => {
        if (upload.id !== slotId) return upload;
        switch (changes.type) {
          case 'progress':
            return {
              ...upload,
              status: 'uploading' as const,
              progress: Math.round(changes.progress * 100),
            };
          case 'success':
            return {
              ...upload,
              status: 'completed' as const,
              progress: 100,
            };
          case 'error':
            return {
              ...upload,
              status: 'failed' as const,
              progress: 0,
              errorMessage: changes.error.message,
            };
          default:
            return upload;
        }
      }),
    );
  }

  private async loadFileDetails(slotId: number): Promise<void> {
    if (!this._uploads().some((upload) => upload.id === slotId)) return;

    try {
      const media = await this.filesApi.getFile(slotId);

      this._uploads.update((uploads) =>
        uploads.map((upload) => {
          if (upload.id !== slotId) return upload;
          return {
            ...upload,
            media,
          };
        }),
      );
    } catch (error) {
      console.error(`Failed to load file details for upload ${slotId}:`, error);
    }
  }
}

export function formatFileSize(size: number): string {
  if (!Number.isFinite(size)) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let unitIndex = 0;
  let value = size;

  while (value >= 1000 && unitIndex < units.length - 1) {
    value /= 1000;
    unitIndex += 1;
  }

  const fractionDigits = unitIndex === 0 ? 0 : 1;

  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} ${units[unitIndex]}`;
}
